import { useUserTemplate } from "@/hooks/useUserTemplate";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import ConcisenessSliderView from "./ConcisenessSliderView";

const CONCISION_PERSIST_DEBOUNCE_MS = 500;

type ConcisenessSliderProps = {
  templateId: string;
  showExample?: boolean;
};

export const ConcisenessSlider = ({ templateId, showExample = false }: ConcisenessSliderProps) => {
  const { userTemplate, updateUserTemplate } = useUserTemplate({ templateId });
  const [intermediateConcision, setIntermediateConcision] = useState(
    userTemplate?.defaultConcision
  );

  const getConcisionExample = useCallback(
    (currentValue?: number): string => {
      const examples = userTemplate?.concisionExamples;
      if (!examples || currentValue === undefined) return "";

      const thresholds = Object.keys(examples)
        .map(Number)
        .sort((a, b) => a - b);

      const threshold =
        thresholds.find((thresholdValue) => currentValue <= thresholdValue) ??
        thresholds[thresholds.length - 1];

      const exampleValue = (examples as Record<string, unknown>)[String(threshold)];
      return typeof exampleValue === "string" ? exampleValue : "";
    },
    [userTemplate?.concisionExamples]
  );

  const debouncedPersistConcision = useMemo(
    () =>
      debounce(async (v: number) => {
        if (!templateId) return;
        try {
          await updateUserTemplate("defaultConcision", v);
          toast.success("New concision level saved successfully");
        } catch (error) {
          console.error("Error updating concision:", error);
          toast.error("Failed to update user concision");
        }
      }, CONCISION_PERSIST_DEBOUNCE_MS),
    [templateId]
  );

  useEffect(() => {
    return () => {
      debouncedPersistConcision.flush();
    };
  }, [debouncedPersistConcision]);

  useEffect(() => {
    setIntermediateConcision(userTemplate?.defaultConcision);
  }, [userTemplate?.defaultConcision]);

  const handleConcisionChange = useCallback(
    (v: number) => {
      setIntermediateConcision(v);
      debouncedPersistConcision(v);
    },
    [debouncedPersistConcision]
  );

  if (intermediateConcision === undefined) {
    return (
      <div aria-busy="true" aria-label="Loading conciseness controls">
        <Skeleton height={20} borderRadius={8} containerClassName="block w-full" />
      </div>
    );
  }

  const exampleText = showExample ? getConcisionExample(intermediateConcision) : "";

  return (
    <ConcisenessSliderView
      value={intermediateConcision}
      onValueChange={handleConcisionChange}
      showExample={showExample}
      exampleText={exampleText}
    />
  );
};
